import * as React from 'react';
import { useCallback, useState } from 'react';
import { Button, Container, FormField, Header, Select, SpaceBetween, Alert } from '@amzn/awsui-components-react';
import getDeviceComponentStatusHandler from '../../services/getDeviceComponentStatusHandler';
import { componentTypes, ComponentType } from './componentType';

interface ComponentCheckProps {
    deviceId: string;
    vin: string;
    onCheckComplete: (
        hasExistingComponent: boolean,
        componentType?: ComponentType,
        existingComponentInfo?: any,
    ) => void;
    onCancel: () => void;
    disabled?: boolean;
}

const ComponentCheck: React.FC<ComponentCheckProps> = ({
    deviceId,
    vin,
    onCheckComplete,
    onCancel,
    disabled = false,
}) => {
    const [componentType, setComponentType] = useState<any>(componentTypes[0]);
    const [isChecking, setIsChecking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCheckDeviceInfo = useCallback(() => {
        if (!componentType) {
            setError('Please select a component type');
            return;
        }

        setIsChecking(true);
        setError(null);

        getDeviceComponentStatusHandler(
            deviceId,
            componentType.value.toUpperCase(),
            undefined,
            (success: boolean, data: any) => {
                setIsChecking(false);

                if (!success) {
                    setError('Failed to check device information. Please try again.');
                    return;
                }

                // Check if there is any existing installation
                let hasExistingComponent = false;
                let existingComponentInfo = null;
                if (data && data.componentId && data.status) {
                    hasExistingComponent = true;
                    existingComponentInfo = data;
                }

                onCheckComplete(hasExistingComponent, componentType, existingComponentInfo);
            },
        );
    }, [componentType, deviceId, vin, onCheckComplete]);

    return (
        <Container>
            <SpaceBetween size="l">
                <Header variant="h2">Check Device Component Status</Header>

                <FormField label="Device ID" description="The ID of the device you're checking">
                    <div>{deviceId}</div>
                </FormField>

                <FormField label="VIN" description="Vehicle Identification Number">
                    <div>{vin}</div>
                </FormField>

                <FormField label="Component Type" description="Select the type of component to check for">
                    <Select
                        selectedOption={componentType}
                        onChange={({ detail }) => {
                            setComponentType(detail.selectedOption as ComponentType);
                            setError(null);
                        }}
                        options={componentTypes}
                        placeholder="Choose a component type"
                        disabled={disabled}
                    />
                </FormField>

                {error && (
                    <Alert type="error" header="Error">
                        {error}
                    </Alert>
                )}

                <SpaceBetween size="xs" direction="horizontal">
                    <Button variant="primary" loading={isChecking} onClick={handleCheckDeviceInfo} disabled={disabled}>
                        Check Device Info
                    </Button>
                    <Button variant="link" onClick={onCancel} disabled={disabled}>
                        Cancel
                    </Button>
                </SpaceBetween>
            </SpaceBetween>
        </Container>
    );
};

export default ComponentCheck;
